import { createApi } from '@reduxjs/toolkit/query/react'
import { BaseRequest, BaseResponse } from '../models'
import { AccountUserSettings } from '../../models/AccountUserSettings'
import { baseQueryWithTokenRefresh } from '../../common/baseQuery'
import { Account, AccountConfig, AccountUpdateOptions } from '../../models/Account'
import { UserOwner } from '../../models/User'

const _BASE_URL = import.meta.env.REACT_APP_IAM_API_BASE_URL!

export interface UpdateAccountRequest extends BaseRequest {
  payload: UpdateAccountRequestPayload
}

export interface UpdateAccountUserSettingsRequest extends BaseRequest {
  payload: UpdateAccountUserSettingsRequestPayload
}

export interface UpdateAccountRequestPayload {
  name?: string
  config?: AccountConfig
  options?: AccountUpdateOptions
}

export interface UpdateAccountUserSettingsRequestPayload {
  settingsOverride: {
    sessionSharingEnabled: boolean
    contactSharingEnabled: boolean
    emailSharingEnabled: boolean
    defaultContactOwnership: UserOwner | null
  }
}

export const DEFAULT_SESSION_SHARING_ENABLED = true
export const DEFAULT_CONTACT_SHARING_ENABLED = false
export const DEFAULT_EMAIL_SHARING_ENABLED = false

export const accountUserSettingsApi = createApi({
  reducerPath: 'accountUserSettingsApi',
  baseQuery: baseQueryWithTokenRefresh(_BASE_URL),
  tagTypes: ['AccountUserSettings', 'Account'],
  endpoints: (builder) => ({
    getAccount: builder.query<BaseResponse<Account>, BaseRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}`,
        }
      },
      providesTags: ['Account'],
    }),
    updateAccount: builder.mutation<BaseResponse<Account>, UpdateAccountRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}`,
        body: request.payload,
        method: 'PATCH',
      }),
      invalidatesTags: ['Account'],
    }),
    getAccountUserSettings: builder.query<BaseResponse<AccountUserSettings>, BaseRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/user-settings`,
        }
      },
      providesTags: ['AccountUserSettings'],
    }),
    updateAccountUserSettings: builder.mutation<BaseResponse<AccountUserSettings>, UpdateAccountUserSettingsRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/user-settings`,
        body: request.payload,
        method: 'PUT',
      }),
      invalidatesTags: ['AccountUserSettings'],
    }),
  }),
})

export const { useGetAccountUserSettingsQuery, useGetAccountQuery, useLazyGetAccountQuery, useUpdateAccountMutation, useUpdateAccountUserSettingsMutation } =
  accountUserSettingsApi
